<template>
  <div class="schedule-workspace" :class="{ 'is-reception': mode === 'reception' }">
    <div class="sw-toolbar">
      <div class="sw-toolbar__nav">
        <template v-if="mode !== 'reception'">
          <button type="button" class="sw-chip sw-chip--icon" title="Anterior" @click="shift(-1)">
            <i class="bi bi-chevron-left"></i>
          </button>
          <button type="button" class="sw-chip sw-chip--icon" title="Próximo" @click="shift(1)">
            <i class="bi bi-chevron-right"></i>
          </button>
          <button type="button" class="sw-chip" @click="goToday">Hoje</button>
        </template>
          <ScheduleFiltersMenu
            :status="filters.status"
            :id-prof="filters.id_prof"
            :id-spec="filters.id_spec"
            :id-paymeth="filters.id_paymeth"
            :id-insurance="filters.id_insurance"
            @update:status="filters.status = $event"
            @update:idProf="filters.id_prof = $event"
            @update:idSpec="filters.id_spec = $event"
            @update:idPaymeth="filters.id_paymeth = $event"
            @update:idInsurance="filters.id_insurance = $event"
            @change-prof="$emit('change-prof', $event)"
            @change-spec="$emit('change-spec')"
            @spec-interval="specInterval = $event"
          />
        <div v-if="mode === 'reception'" class="sw-toolbar__prof">
          <ScheduleFilterSelect
            :model-value="filters.id_prof"
            :options="professionals"
            option-label="prof_name"
            option-value="id_prof"
            placeholder="Profissional"
            searchable
            show-clear
            @update:modelValue="filters.id_prof = $event"
            @change="onToolbarProf"
          />
        </div>
      </div>

      <div class="sw-toolbar__main">
        <div v-if="mode !== 'reception'" class="sw-toolbar__range">
          <div class="sw-chip-group">
            <button
              v-for="item in ranges"
              :key="item.id"
              type="button"
              class="sw-chip"
              :class="{ 'is-active': mode === 'agenda' && range === item.id }"
              @click="setRange(item.id)"
            >
              {{ item.label }}
            </button>
          </div>
          <div class="sw-toolbar__period">{{ label }}</div>
        </div>

        <div class="sw-toolbar__modes">
          <div class="sw-chip-group">
            <button
              type="button"
              class="sw-chip"
              :class="{ 'is-active': mode === 'agenda' }"
              @click="setMode('agenda')"
            >
              Agenda
            </button>
            <button
              type="button"
              class="sw-chip"
              :class="{ 'is-active': mode === 'reception' }"
              @click="setMode('reception')"
            >
              Recepção
            </button>
            <button
              type="button"
              class="sw-chip"
              title="Recepção Multiprofissional"
              @click="$emit('open-board')"
            >
              Recepção Multiprofissional
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="sw-grid" :class="{ 'is-full': mode === 'reception' }">
      <aside v-if="mode !== 'reception'">
        <ScheduleMiniCalendar
          :selected-date="selectedDate"
          :id-prof="filters.id_prof"
          :id-spec="filters.id_spec"
          :professionals="professionals"
          @select-date="onSelectDate"
          @update:idProf="filters.id_prof = $event"
          @change-prof="onToolbarProf"
        />
        <div class="sw-notes mt-3" v-if="hasPermission('List Note') && filters.id_prof">
          <Notes :profId="filters.id_prof" :specId="filters.id_spec" />
        </div>
      </aside>

      <section class="sw-panel">
        <ScheduleStatsBar :stats="stats" @download="downloadList" />

        <ScheduleReceptionBoard
          v-if="mode === 'reception'"
          :schedules="daySchedules"
          @open-schedule="$emit('open-schedule', $event)"
          @take-picture="photoSchedule = $event"
          @open-recurrence="$emit('open-recurrence', $event)"
          @arrive="applyArrive"
          @finish="applyFinish"
          @reopen="applyReopen"
          @start="applyStart"
        />
        <ScheduleWeekGrid
          v-else-if="range === 'week'"
          :days="weekDates"
          :schedules="visibleSchedules"
          :selected-date="selectedDate"
          :id-prof="filters.id_prof"
          :loading="loading"
          @select-day="openDay"
          @open-schedule="$emit('open-schedule', $event)"
        />
        <ScheduleMonthGrid
          v-else-if="range === 'month'"
          :cells="monthDates"
          :schedules="visibleSchedules"
          :selected-date="selectedDate"
          :id-prof="filters.id_prof"
          :loading="loading"
          @select-day="openDay"
          @open-schedule="$emit('open-schedule', $event)"
        />
        <ScheduleDayTimeline
          v-else
          :schedules="daySchedules"
          :availability-slots="dayAvailabilitySlots"
          :day-range="dayAvailabilityRange"
          :selected-date="selectedDate"
          :professional="professional"
          :step-minutes="specInterval || professional?.interval_minutes"
          :id-prof="filters.id_prof"
          :loading="loading"
          :can-block="hasPermission('Store Availability Block')"
          :can-unblock="hasPermission('Delete Availability Block')"
          @open-schedule="$emit('open-schedule', $event)"
          @take-picture="photoSchedule = $event"
          @open-recurrence="$emit('open-recurrence', $event)"
          @arrive="applyArrive"
          @finish="applyFinish"
          @reopen="applyReopen"
          @start="applyStart"
          @create-at="$emit('create-at', $event)"
          @block-at="openBlockSlot"
          @unblock-at="openUnblockSlot"
        />
      </section>
    </div>

    <SchedulePrintListModal
      :open="printModalOpen"
      :place-id="place?.id_place"
      :id-prof="filters.id_prof"
      :id-spec="filters.id_spec"
      :start-date="printStart"
      :end-date="printEnd"
      @close="printModalOpen = false"
    />
    <ScheduleBlockSlotModal
      :open="blockModalOpen"
      :datetime="blockSlot && blockSlot.datetime"
      :step-minutes="blockSlot && blockSlot.step ? blockSlot.step : 15"
      :id-prof="filters.id_prof"
      @close="closeBlockSlot"
      @saved="$emit('refresh')"
    />
    <ScheduleUnblockModal
      :open="unblockModalOpen"
      :block-ids="unblockTarget && unblockTarget.ids ? unblockTarget.ids : []"
      :label="unblockTarget && unblockTarget.label ? unblockTarget.label : ''"
      :id-prof="filters.id_prof"
      @close="closeUnblockSlot"
      @saved="$emit('refresh')"
    />
    <SchedulePatientPhotoModal
      :schedule="photoSchedule"
      @close="photoSchedule = null"
      @saved="$emit('refresh')"
    />
  </div>
</template>

<script>
import Permission from '../../../helpers/permission';
import moment from '../../../helpers/date';
import Alert from '../../../helpers/alert';
import {
  addPeriod,
  dayStats,
  filterPatientSchedules,
  loadAvailabilityDayRanges,
  loadNextScheduleDates,
  loadPeriodSchedules,
  monthCells,
  periodBounds,
  periodLabel,
  printBounds,
  schedulesOnDate,
  toIsoDate,
  weekDays,
} from '../../../helpers/scheduleDay';
import {
  loadScheduleWorkspacePrefs,
  saveScheduleWorkspacePrefs,
} from '../../../helpers/scheduleWorkspacePrefs';
import './scheduleWorkspace.css';
import Notes from '../Notes.vue';
import ScheduleDayTimeline from './ScheduleDayTimeline.vue';
import ScheduleFilterSelect from './ScheduleFilterSelect.vue';
import ScheduleFiltersMenu from './ScheduleFiltersMenu.vue';
import ScheduleMiniCalendar from './ScheduleMiniCalendar.vue';
import ScheduleMonthGrid from './ScheduleMonthGrid.vue';
import SchedulePatientPhotoModal from './SchedulePatientPhotoModal.vue';
import ScheduleBlockSlotModal from './ScheduleBlockSlotModal.vue';
import ScheduleUnblockModal from './ScheduleUnblockModal.vue';
import SchedulePrintListModal from './SchedulePrintListModal.vue';
import ScheduleReceptionBoard from './ScheduleReceptionBoard.vue';
import ScheduleStatsBar from './ScheduleStatsBar.vue';
import ScheduleWeekGrid from './ScheduleWeekGrid.vue';

export default {
  name: 'ScheduleWorkspace',
  components: {
    Notes,
    ScheduleDayTimeline,
    ScheduleFilterSelect,
    ScheduleFiltersMenu,
    ScheduleMiniCalendar,
    ScheduleMonthGrid,
    ScheduleBlockSlotModal,
    ScheduleUnblockModal,
    SchedulePatientPhotoModal,
    SchedulePrintListModal,
    ScheduleReceptionBoard,
    ScheduleStatsBar,
    ScheduleWeekGrid,
  },
  props: {
    filters: { type: Object, required: true },
    professional: { type: Object, default: null },
    uniqueKey: { default: null },
  },
  emits: [
    'change-prof',
    'change-spec',
    'open-schedule',
    'open-recurrence',
    'create-at',
    'open-board',
    'refresh',
  ],
  data() {
    const prefs = loadScheduleWorkspacePrefs({
      userId: this.$store.state.auth.user?.id_user,
      placeId: this.$store.state.auth.place?.id_place,
    });
    return {
      selectedDate: moment().format('YYYY-MM-DD'),
      range: prefs.range,
      mode: prefs.mode || 'agenda',
      ranges: [
        { id: 'day', label: 'Dia' },
        { id: 'week', label: 'Semana' },
        { id: 'month', label: 'Mês' },
      ],
      periodSchedules: [],
      availabilitySlots: [],
      availabilityRanges: [],
      specInterval: null,
      loading: false,
      photoSchedule: null,
      printModalOpen: false,
      blockSlot: null,
      blockModalOpen: false,
      unblockTarget: null,
      unblockModalOpen: false,
      boundQueueProf: null,
      skipRealtimeUntil: 0,
      nextDateWithSchedule: null,
      prevDateWithSchedule: null,
    };
  },
  computed: {
    prefsScope() {
      return {
        userId: this.$store.state.auth.user?.id_user,
        placeId: this.$store.state.auth.place?.id_place,
      };
    },
    place() {
      return this.$store.state.auth.place;
    },
    label() {
      return periodLabel(this.selectedDate, this.mode === 'reception' ? 'day' : this.range);
    },
    weekDates() {
      return weekDays(this.selectedDate);
    },
    monthDates() {
      return monthCells(this.selectedDate);
    },
    visibleSchedules() {
      return filterPatientSchedules(this.periodSchedules, {
        idPaymeth: this.filters.id_paymeth,
        idInsurance: this.filters.id_insurance,
      });
    },
    daySchedules() {
      return schedulesOnDate(this.visibleSchedules, this.selectedDate);
    },
    dayAvailabilitySlots() {
      return (this.availabilitySlots || []).filter((slot) => toIsoDate(slot.start) === this.selectedDate);
    },
    dayAvailabilityRange() {
      return (this.availabilityRanges || []).find((item) => item.available_date === this.selectedDate) || null;
    },
    statsSource() {
      if (this.mode === 'reception' || this.range === 'day') return this.daySchedules;
      return this.visibleSchedules;
    },
    stats() {
      return dayStats(this.statsSource);
    },
    professionals() {
      return this.$store.state.auth.professionals || [];
    },
    printRange() {
      const range = this.mode === 'reception' ? 'day' : this.range;
      return printBounds(this.selectedDate, range);
    },
    printStart() {
      return this.printRange.start;
    },
    printEnd() {
      return this.printRange.end;
    },
  },
  watch: {
    uniqueKey() {
      this.reloadVisible({ silent: true });
    },
    'filters.id_prof'() {
      this.reloadVisible();
      this.bindQueue();
    },
    'filters.id_spec'() {
      this.reloadVisible();
    },
    'filters.status'() {
      this.reloadVisible();
    },
    selectedDate() {
      this.reloadVisible();
    },
    range() {
      this.persistPrefs();
      this.reloadVisible();
    },
    mode() {
      this.persistPrefs();
      this.reloadVisible();
    },
  },
  mounted() {
    this.reloadVisible();
    this.bindQueue();
  },
  beforeUnmount() {
    this.persistPrefs();
    this.unbindQueue();
  },
  methods: {
    hasPermission(permission) {
      return Permission.has(permission);
    },
    onToolbarProf(idProf) {
      const professional = this.professionals.find((item) => item.id_prof === idProf) || null;
      this.$emit('change-prof', professional);
    },
    persistPrefs() {
      saveScheduleWorkspacePrefs({ range: this.range, mode: this.mode }, this.prefsScope);
    },
    setRange(range) {
      this.range = range;
      this.mode = 'agenda';
      this.persistPrefs();
    },
    setMode(mode) {
      this.mode = mode === 'reception' ? 'reception' : 'agenda';
      if (this.mode === 'reception') {
        this.selectedDate = moment().format('YYYY-MM-DD');
      }
      this.persistPrefs();
    },
    shift(delta) {
      const range = this.mode === 'reception' ? 'day' : this.range;
      if (this.mode === 'agenda' && range === 'day') {
        const jump = delta > 0 ? this.nextDateWithSchedule : this.prevDateWithSchedule;
        if (jump && jump !== this.selectedDate) {
          this.selectedDate = jump;
          return;
        }
      }
      this.selectedDate = addPeriod(this.selectedDate, range, delta);
    },
    goToday() {
      this.selectedDate = moment().format('YYYY-MM-DD');
    },
    onSelectDate(value) {
      this.selectedDate = toIsoDate(value);
    },
    openDay(day) {
      this.selectedDate = day;
      this.range = 'day';
      this.mode = 'agenda';
      this.persistPrefs();
    },
    reloadVisible({ silent = false } = {}) {
      const range = this.mode === 'reception' ? 'day' : this.range;
      const { start, end } = periodBounds(this.selectedDate, range);
      if (!silent) this.loading = true;
      Promise.all([
        loadPeriodSchedules(this.place?.id_place, {
          idProf: this.filters.id_prof,
          idSpec: this.filters.id_spec,
          status: this.filters.status,
          start,
          end,
        }),
        loadAvailabilityDayRanges(this.place?.id_place, {
          idProf: this.filters.id_prof,
          start,
          end,
        }),
      ]).then(([payload, ranges]) => {
        this.periodSchedules = payload.schedules;
        this.availabilitySlots = payload.availabilitySlots;
        this.availabilityRanges = ranges;
        this.refreshAdjacentScheduleDates(start, end);
      }).finally(() => {
        this.loading = false;
      });
    },
    refreshAdjacentScheduleDates(start, end) {
      return loadNextScheduleDates(this.place?.id_place, {
        idProf: this.filters.id_prof,
        idSpec: this.filters.id_spec,
        status: this.filters.status,
        start,
        end,
      }).then((dates) => {
        this.nextDateWithSchedule = dates.next_date;
        this.prevDateWithSchedule = dates.prev_date;
      });
    },
    openBlockSlot(payload) {
      this.blockSlot = payload || null;
      this.blockModalOpen = Boolean(payload);
    },
    closeBlockSlot() {
      this.blockModalOpen = false;
      this.blockSlot = null;
    },
    openUnblockSlot(payload) {
      this.unblockTarget = payload || null;
      this.unblockModalOpen = Boolean(payload && payload.ids && payload.ids.length);
    },
    closeUnblockSlot() {
      this.unblockModalOpen = false;
      this.unblockTarget = null;
    },
    downloadList() {
      if (!this.filters.id_prof) {
        Alert.toastWarning('Selecione um profissional para imprimir a agenda.');
        return;
      }
      this.printModalOpen = true;
    },
    bindQueue() {
      this.unbindQueue();
      const idProf = this.filters.id_prof;
      if (!idProf || typeof Echo === 'undefined') return;
      this.boundQueueProf = idProf;
      Echo.private(`professionals.${idProf}.queues`)
        .listen('ScheduleArrived', this.onRealtime)
        .listen('ScheduleStarted', this.onRealtime)
        .listen('ScheduleFinished', this.onRealtime)
        .listen('ScheduleNotArrived', this.onRealtime);
    },
    unbindQueue() {
      if (!this.boundQueueProf || typeof Echo === 'undefined') return;
      Echo.leave(`professionals.${this.boundQueueProf}.queues`);
      this.boundQueueProf = null;
    },
    onRealtime() {
      if (Date.now() < this.skipRealtimeUntil) return;
      this.reloadVisible({ silent: true });
    },
    findSchedule(id) {
      return this.periodSchedules.find((item) => String(item.id_sched) === String(id));
    },
    stamp() {
      return moment().format('YYYY-MM-DD HH:mm:ss');
    },
    touchSchedule() {
      this.skipRealtimeUntil = Date.now() + 2500;
    },
    applyArrive(id) {
      const item = this.findSchedule(id);
      if (!item) return;
      this.touchSchedule();
      item.sched_arrive = item.sched_arrive ? null : this.stamp();
    },
    applyStart(id) {
      const item = this.findSchedule(id);
      if (!item || item.sched_start) return;
      this.touchSchedule();
      item.sched_start = this.stamp();
    },
    applyFinish(id) {
      const item = this.findSchedule(id);
      if (!item) return;
      this.touchSchedule();
      item.sched_status = 'FI';
      item.is_sched_finished = true;
      item.sched_end = this.stamp();
    },
    applyReopen(id) {
      const item = this.findSchedule(id);
      if (!item) return;
      this.touchSchedule();
      item.sched_status = item.confirmed_at ? 'CO' : 'AG';
      item.is_sched_finished = false;
      item.sched_end = null;
    },
  },
};
</script>

